// Kundens konto efter en booking: alle kundens mails om bookingen (LOGIN_HAENDELSER og "mangler tid") får knappen "Se og ret din booking", der logger kunden ind med et
// engangslink (udløber, kan kun bruges én gang). Første gang en kunde booker, fortæller bekræftelsen også, at kontoen er
// lavet (medKontoIntro) – kunden får kun én mail. "Din kundekonto er klar" sendes for sig selv kun første gang og kun, når
// ingen bekræftelse med login-knappen går ud (fx når bekræftelsen er slået fra). Et nyt login-link gør det forrige ugyldigt,
// så to mails med links lige efter hinanden ville give kunden et dødt link.
// Testversionen laver linket selv (#/link/<token>); i drift laver send-mail et Supabase-login-link (magic link), når mailen sendes.
// Den gemte kopi af mailen har linket skiftet ud med LINK_PLADSHOLDER. Intet sendes, når kundelogin er slået fra, kunden ikke har en e-mail, eller udsendelsen er spærret (klienter.udsendelse_spaerret).
import type { Haendelse } from './automation.ts';
import type { MailTekst } from './pixieMails.ts';

/** Så længe virker kundens login-link: 24 timer (Supabase Auths OTP-udløb i drift er sat til 86400 sekunder). Testversionen
 *  bruger det samme, og mailen lover aldrig mere. */
export const KUNDE_LINK_TIMER = 24;
/** Det, der gemmes i stedet for linket (sendte beskeder, log): kun selve mailen til kunden har det rigtige link, så kontoret
 *  aldrig kan se eller bruge kundens login. */
export const LINK_PLADSHOLDER = '[personligt login-link]';
export const skjulLink = (tekst: string, link: string) => tekst.split(link).join(LINK_PLADSHOLDER);
export const KUNDE_KNAP = 'Se og ret din booking';
/** Mailens skabelonnavn (beskeder.skabelon). */
export const KUNDEKONTO_SKABELON = 'Kundekonto klar';
export const KUNDELINK_SKABELON = 'Se din booking';
/** Bekræftelserne (modtaget og godkendt) – første gang fortæller de også, at kundekontoen er lavet. */
export const BEKRAEFTELSER: Haendelse[] = ['booking_oprettet', 'booking_godkendt'];
/** Kundens mails om en booking, der altid får login-knappen (også hvis virksomheden har rettet teksten, så linket mangler):
 *  modtaget, bekræftet, flyttet og påmindelser (tid før/efter starten). "Mangler tid"-mailen får den også (manglerTid.ts). */
export const LOGIN_HAENDELSER: Haendelse[] = [...BEKRAEFTELSER, 'booking_flyttet', 'booking_start'];

/** Linket i testversionen. I drift: virksomhedens adresse + ?token_hash=…&type=magiclink#/link. */
export const demoLoginLink = (base: string, token: string) => `${base}/#/link/${token}`;

/** Står linket ikke i mailen (fx en rettet skabelon uden {{link_mine}}), sættes det ind før "Spørgsmål?"/hilsenen. */
export function medLoginLink(tekst: string, link: string): string {
  if (tekst.includes(link)) return tekst;
  const afsnit = tekst.split(/\n\s*\n/);
  const i = afsnit.findIndex((a) => /^(spørgsmål\?|venlig hilsen|med venlig hilsen|mvh|de bedste hilsner|hilsen)/i.test(a.trim()));
  const nyt = `Se, flyt eller aflys din booking her – du bliver logget ind med det samme:\n${link}`;
  if (i < 0) afsnit.push(nyt); else afsnit.splice(i, 0, nyt);
  return afsnit.join('\n\n');
}

export type KundeKontoInd = {
  ny: boolean;                 // første booking: "Din kundekonto er klar"; ellers en kort mail med login-knappen
  navn: string; firma: string; link: string;
  booking?: { nr: number | string; ydelse: string; dato: string; tid: string; adresse: string } | null;
};

const timerTekst = (t: number) => (t === 1 ? '1 time' : `${t} timer`);
export const kontoIntro = (firma: string) => `Vi har lavet en kundekonto til dig hos ${firma}, så du selv kan se, flytte og betale dine bookinger – uden at huske en adgangskode.`;

/** Første booking: bekræftelsen fortæller, at kundekontoen er lavet – lige før afsnittet med login-linket. */
export function medKontoIntro(tekst: string, link: string, firma: string): string {
  const afsnit = tekst.split(/\n\s*\n/);
  const i = afsnit.findIndex((a) => a.includes(link));
  afsnit.splice(i < 0 ? afsnit.length : i, 0, kontoIntro(firma));
  return afsnit.join('\n\n');
}

/** Mailen med login-knappen. Teksten følger brandet (firma = brandets navn, fx SteamFox). */
export function kundeKontoMail(v: KundeKontoInd): MailTekst {
  const fornavn = v.navn.trim().split(/\s+/)[0] ?? '';
  const hej = fornavn ? `Hej ${fornavn}` : 'Hej';
  const b = v.booking;
  const detaljer = b ? `\n\nOpgave: ${b.ydelse}\nDato: ${b.dato}, ${b.tid}\nAdresse: ${b.adresse}\nBookingnr.: ${b.nr}` : '';
  const fod = `\n\nDu får denne mail, fordi du har booket hos ${v.firma}. Linket virker i ${timerTekst(KUNDE_LINK_TIMER)} og kun én gang – bagefter logger du ind med din e-mail og en engangskode.\n\nVenlig hilsen\n${v.firma}`;
  return v.ny ? {
    emne: 'Din kundekonto er klar',
    knap: KUNDE_KNAP,
    tekst: `${hej}\n\nTak for din booking. ${kontoIntro(v.firma)}${detaljer}\n\nTryk på knappen, så er du logget ind:\n${v.link}${fod}`,
  } : {
    emne: `Se og ret din booking hos ${v.firma}`,
    knap: KUNDE_KNAP,
    tekst: `${hej}\n\nTak for din booking.${detaljer}\n\nSe, flyt eller aflys den i din kundekonto – du bliver logget ind med det samme:\n${v.link}${fod}`,
  };
}
